import { graphqlRequest } from "./apiClient";

const CREATE_TENANT_MUTATION = `
  mutation CreateTenant($input: CreateTenantInput!) {
    createTenant(input: $input) {
      tenant {
        tenant_id
        name
        slug
        email
        plan
        sector_ipa
        employee_count
        monthly_invoice_volume
        industry
        size
        status
        created_at
        ingest_email
        storage_prefix
        fiscal_cuit
        fiscal_legal_name
        fiscal_iva_condition
        fiscal_profile_completed_at
      }
      prompts_seeded
    }
  }
`;

const UPDATE_TENANT_FISCAL_PROFILE = `
  mutation UpdateTenantFiscalProfile($input: UpdateTenantFiscalProfileInput!) {
    updateTenantFiscalProfile(input: $input) {
      tenant_id
      name
      fiscal_cuit
      fiscal_legal_name
      fiscal_iva_condition
      fiscal_profile_completed_at
      my_role
    }
  }
`;

const UPDATE_TENANT_CONTACT = `
  mutation UpdateTenantContact($input: UpdateTenantContactInput!) {
    updateTenantContact(input: $input) {
      tenant_id
      name
      status
      contact {
        name
        email
        phone
        whatsapp
        social {
          linkedin
          instagram
          website
          other_label
          other_value
        }
      }
    }
  }
`;

const SUBMIT_TENANT_FOR_APPROVAL = `
  mutation SubmitTenantForApproval {
    submitTenantForApproval {
      tenant {
        tenant_id
        name
        status
        submitted_for_approval_at
        sector_ipa
        employee_count
        monthly_invoice_volume
        contact {
          name
          email
          phone
          whatsapp
        }
      }
      notification_sent
    }
  }
`;

const MY_TENANT_QUERY = `
  query MyTenant {
    myTenant {
      tenant_id
      name
      status
      sector_ipa
      employee_count
      monthly_invoice_volume
      ingest_email
      storage_prefix
      fiscal_cuit
      fiscal_legal_name
      fiscal_iva_condition
      fiscal_profile_completed_at
      submitted_for_approval_at
      contact {
        name
        email
        phone
        whatsapp
        social {
          linkedin
          instagram
          website
          other_label
          other_value
        }
      }
    }
  }
`;

const INVITE_TENANT_USERS_MUTATION = `
  mutation InviteTenantUsers($tenantId: ID!, $invitations: [TenantUserInviteInput!]!) {
    inviteTenantUsers(tenantId: $tenantId, invitations: $invitations) {
      invitations {
        email
        role
        status
      }
    }
  }
`;

function formatGraphqlError(err) {
  if (typeof err === "string") {
    try {
      const parsed = JSON.parse(err);
      if (Array.isArray(parsed) && parsed[0]?.message) {
        return parsed[0].message;
      }
    } catch {
      return err;
    }
    return err;
  }
  return err?.message || "Error de API";
}

export async function createTenant(payload) {
  try {
    const data = await graphqlRequest(CREATE_TENANT_MUTATION, { input: payload });
    return {
      tenant: data.createTenant.tenant,
      promptsSeeded: data.createTenant.prompts_seeded ?? 0,
    };
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}

const SYNC_TENANT_TO_HUBSPOT = `
  mutation SyncTenantToHubSpot($input: SyncTenantToHubSpotInput) {
    syncTenantToHubSpot(input: $input) {
      deduplicated
      sync {
        sync_id
        status
        sync_timestamp
        hubspot_contact_id
        hubspot_deal_id
        error_message
      }
    }
  }
`;

export async function updateTenantContact(input) {
  try {
    const data = await graphqlRequest(UPDATE_TENANT_CONTACT, { input });
    return data.updateTenantContact;
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}

/**
 * SPEC-20 — enqueue HubSpot Contact+Deal sync (non-blocking for callers).
 * @param {{ contact_email?: string, contact_phone?: string, contact_name?: string, force?: boolean }} [input]
 */
export async function syncTenantToHubSpot(input = {}) {
  try {
    const data = await graphqlRequest(SYNC_TENANT_TO_HUBSPOT, { input });
    return data.syncTenantToHubSpot;
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}

export async function submitTenantForApproval() {
  try {
    const data = await graphqlRequest(SUBMIT_TENANT_FOR_APPROVAL);
    return data.submitTenantForApproval;
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}

export async function inviteUsers(tenantId, invitations) {
  try {
    const data = await graphqlRequest(INVITE_TENANT_USERS_MUTATION, {
      tenantId,
      invitations,
    });
    return { invitations: data.inviteTenantUsers.invitations };
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}

export async function getTenant(_tenantId) {
  try {
    const data = await graphqlRequest(MY_TENANT_QUERY);
    return data.myTenant;
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}

export async function updateTenantFiscalProfile(input) {
  try {
    const data = await graphqlRequest(UPDATE_TENANT_FISCAL_PROFILE, { input });
    return data.updateTenantFiscalProfile;
  } catch (e) {
    throw new Error(formatGraphqlError(e.message || e));
  }
}
